import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ActionItem, User } from '../types';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import Select from './Select';

interface ActionItemsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  participants: { user: User }[];
  inline?: boolean;
}

type Priority = 'low' | 'medium' | 'high' | 'urgent';
type Status = 'todo' | 'in_progress' | 'done' | 'cancelled';

const priorityConfig: Record<Priority, { label: string; dot: string }> = {
  low: { label: 'Low', dot: 'bg-slate-400' },
  medium: { label: 'Medium', dot: 'bg-orange-400' },
  high: { label: 'High', dot: 'bg-amber-400' },
  urgent: { label: 'Urgent', dot: 'bg-red-400' },
};

const statusConfig: Record<Status, { label: string; color: string }> = {
  todo: { label: 'To Do', color: 'text-slate-500' },
  in_progress: { label: 'In Progress', color: 'text-orange-500' },
  done: { label: 'Done', color: 'text-emerald-600' },
  cancelled: { label: 'Cancelled', color: 'text-red-500' },
};

const StatusIcon: React.FC<{ status: Status; className?: string }> = ({ status, className = 'w-4 h-4' }) => {
  switch (status) {
    case 'todo':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" strokeWidth={2} strokeDasharray="4 2" />
        </svg>
      );
    case 'in_progress':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'done':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'cancelled':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

const ActionItemsPanel: React.FC<ActionItemsPanelProps> = ({ isOpen, onClose, boardId, participants, inline }) => {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null);
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const { showSuccess, showError } = useToast();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const getToken = () => useAuthStore.getState().tokens?.access;

  const fetchActionItems = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/action-items/board/${boardId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionItems(response.data.action_items || response.data.results || []);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      showError(axiosError.response?.data?.detail || 'Failed to load action items');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, boardId, showError]);

  useEffect(() => {
    if (isOpen && boardId) {
      fetchActionItems();
    }
  }, [isOpen, boardId, fetchActionItems]);

  const createActionItem = async (data: Partial<ActionItem>) => {
    try {
      const token = getToken();
      const payload = { ...data, board: boardId };
      if (!payload.due_date) delete payload.due_date;
      await axios.post(`${API_BASE_URL}/action-items/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchActionItems();
      setShowCreateForm(false);
      showSuccess('Action item created');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { detail?: string; due_date?: string[] } } };
      showError(
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.due_date?.[0] ||
        'Failed to create action item'
      );
    }
  };

  const updateActionItem = async (id: string, data: Partial<ActionItem>) => {
    try {
      const token = getToken();
      const payload = { ...data };
      if (!payload.due_date) delete payload.due_date;
      await axios.patch(`${API_BASE_URL}/action-items/${id}/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchActionItems();
      setEditingItem(null);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      showError(axiosError.response?.data?.detail || 'Failed to update action item');
    }
  };

  const deleteActionItem = async (id: string) => {
    if (!window.confirm('Delete this action item?')) return;
    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/action-items/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchActionItems();
      showSuccess('Deleted');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      showError(axiosError.response?.data?.detail || 'Failed to delete');
    }
  };

  const doneCount = actionItems.filter(i => i.status === 'done').length;
  const filteredItems = filter === 'all'
    ? actionItems
    : actionItems.filter(item => item.status === filter);

  if (inline) {
    return (
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 text-sm">Action Items</span>
            {actionItems.length > 0 && (
              <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[11px] text-slate-500 tabular-nums">
                {doneCount}/{actionItems.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="px-3 py-2.5 border-b border-slate-200 flex gap-1 overflow-x-auto shrink-0">
          <button
            onClick={() => setFilter('all')}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              filter === 'all'
                ? 'bg-orange-500 text-white'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            All
          </button>
          {(Object.keys(statusConfig) as Status[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                filter === status
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <StatusIcon status={status} className="w-3 h-3" />
              {statusConfig[status].label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 pr-10">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-sm text-slate-400">
                {filter === 'all' ? 'No action items yet' : `No ${statusConfig[filter as Status]?.label.toLowerCase()} items`}
              </p>
              {filter === 'all' && (
                <p className="text-xs text-slate-400 mt-1">Add one below to get started</p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredItems.map((item) => (
                <ActionItemCard
                  key={item.id}
                  item={item}
                  onEdit={() => setEditingItem(item)}
                  onStatusChange={(status) => updateActionItem(item.id, { status })}
                  onDelete={() => deleteActionItem(item.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 shrink-0">
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm rounded-xl
                       transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New action item
          </button>
        </div>

        {(showCreateForm || editingItem) && (
          <ActionItemForm
            item={editingItem}
            participants={participants}
            onSave={(data) => editingItem ? updateActionItem(editingItem.id, data) : createActionItem(data)}
            onClose={() => {
              setShowCreateForm(false);
              setEditingItem(null);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />}

      <div
        className={`fixed top-0 right-0 h-full w-[420px] max-w-full bg-white shadow-2xl z-50
          flex flex-col transition-transform duration-300 ease-in-out border-l border-slate-200
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 text-sm">Action Items</span>
            {actionItems.length > 0 && (
              <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[11px] text-slate-500 tabular-nums">
                {doneCount}/{actionItems.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="px-3 py-2.5 border-b border-slate-200 flex gap-1 overflow-x-auto shrink-0">
          <button
            onClick={() => setFilter('all')}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              filter === 'all'
                ? 'bg-orange-500 text-white'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            All
          </button>
          {(Object.keys(statusConfig) as Status[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                filter === status
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <StatusIcon status={status} className="w-3 h-3" />
              {statusConfig[status].label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 pr-10">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-sm text-slate-400">
                {filter === 'all' ? 'No action items yet' : `No ${statusConfig[filter as Status]?.label.toLowerCase()} items`}
              </p>
              {filter === 'all' && (
                <p className="text-xs text-slate-400 mt-1">Add one below to get started</p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredItems.map((item) => (
                <ActionItemCard
                  key={item.id}
                  item={item}
                  onEdit={() => setEditingItem(item)}
                  onStatusChange={(status) => updateActionItem(item.id, { status })}
                  onDelete={() => deleteActionItem(item.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 shrink-0">
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm rounded-xl
                       transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New action item
          </button>
        </div>

        {/* Create/Edit Form Modal */}
        {(showCreateForm || editingItem) && (
          <ActionItemForm
            item={editingItem}
            participants={participants}
            onSave={(data) => editingItem ? updateActionItem(editingItem.id, data) : createActionItem(data)}
            onClose={() => {
              setShowCreateForm(false);
              setEditingItem(null);
            }}
          />
        )}
      </div>
    </>
  );
};

interface ActionItemCardProps {
  item: ActionItem;
  onEdit: () => void;
  onStatusChange: (status: Status) => void;
  onDelete: () => void;
}

const ActionItemCard: React.FC<ActionItemCardProps> = ({ item, onEdit, onStatusChange, onDelete }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const isDone = item.status === 'done';

  return (
    <div className={`rounded-xl border p-3 transition-all group ${
      isDone
        ? 'bg-slate-50 border-slate-100'
        : 'bg-white border-slate-200 hover:border-orange-200'
    }`}>
      <div className="flex items-start gap-2.5">
        {/* Quick done toggle */}
        <button
          onClick={() => onStatusChange(isDone ? 'todo' : 'done')}
          className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
            isDone
              ? 'bg-emerald-500 border-emerald-500'
              : 'border-slate-300 hover:border-orange-400'
          }`}
          title={isDone ? 'Mark as todo' : 'Mark as done'}
        >
          {isDone && (
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-medium leading-snug transition-colors ${
              isDone ? 'line-through text-slate-400' : 'text-slate-800 group-hover:text-slate-900'
            }`}>
              {item.title}
            </h4>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              <button
                onClick={onEdit}
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                onClick={onDelete}
                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          {item.description && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.description}</p>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Status badge (non-done items) */}
            {!isDone && item.status !== 'todo' && (
              <div className="relative">
                <button
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  className={`flex items-center gap-1 text-[11px] font-medium transition-colors ${statusConfig[item.status as Status].color}`}
                >
                  <StatusIcon status={item.status as Status} className="w-3 h-3" />
                  {statusConfig[item.status as Status].label}
                </button>
                {showStatusMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowStatusMenu(false)} />
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 z-20 py-1 min-w-[140px]">
                      {(Object.keys(statusConfig) as Status[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => { onStatusChange(status); setShowStatusMenu(false); }}
                          className={`w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 flex items-center gap-2 transition-colors ${statusConfig[status].color}`}
                        >
                          <StatusIcon status={status} className="w-3.5 h-3.5" />
                          {statusConfig[status].label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Priority dot */}
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <span className={`w-1.5 h-1.5 rounded-full ${priorityConfig[item.priority as Priority]?.dot}`} />
              {priorityConfig[item.priority as Priority]?.label}
            </span>

            {/* Due date */}
            {item.due_date && (
              <span className="text-[11px] text-slate-500">
                {new Date(item.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            )}

            {/* Assignee avatar */}
            {item.assigned_to && (
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold"
                style={{ backgroundColor: item.assigned_to.avatar_color }}
                title={item.assigned_to.display_name}
              >
                {item.assigned_to.initials}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ActionItemFormProps {
  item: ActionItem | null;
  participants: { user: User }[];
  onSave: (data: Partial<ActionItem>) => void;
  onClose: () => void;
}

const ActionItemForm: React.FC<ActionItemFormProps> = ({ item, participants, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    description: item?.description || '',
    priority: item?.priority || 'medium',
    status: item?.status || 'todo',
    due_date: item?.due_date || '',
    assigned_to_id: item?.assigned_to?.id || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    onSave({ ...formData, assigned_to_id: formData.assigned_to_id || undefined } as Partial<ActionItem>);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">
            {item ? 'Edit action item' : 'New action item'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3.5">
          <div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400
                         focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors outline-none"
              placeholder="What needs to be done?"
              autoFocus
              required
            />
          </div>

          <div>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400
                         focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors outline-none resize-none"
              rows={2}
              placeholder="Add details (optional)"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Priority</label>
              <Select
                value={formData.priority}
                onChange={(v) => setFormData({ ...formData, priority: v as Priority })}
                options={(Object.keys(priorityConfig) as Priority[]).map((p) => ({ value: p, label: priorityConfig[p].label }))}
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Due date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900
                           focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
              />
            </div>
          </div>

          {participants.length > 0 && (
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Assign to</label>
              <Select
                value={formData.assigned_to_id}
                onChange={(v) => setFormData({ ...formData, assigned_to_id: v })}
                placeholder="Unassigned"
                options={participants.map((p) => ({ value: p.user.id, label: p.user.display_name }))}
              />
            </div>
          )}

          <div className="flex gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm rounded-xl border border-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm rounded-xl transition-colors"
            >
              {item ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActionItemsPanel;
